import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const TOUR_STEPS = [
  {
    target: 'global-network',
    path: '/dashboard',
    title: 'Global Network Score',
    description: 'See your overall security score, its contributing signals, and how your organization compares across the Risqly network.',
  },
  {
    target: 'verification-check',
    path: '/dashboard',
    title: 'Get Fully Verified',
    description: 'Your recommended first action is to link your Risqly company page from your website and schedule the verification check here. This confirms ownership and upgrades your company to fully verified.',
  },
  {
    target: 'policy-maturity',
    path: '/dashboard/policy-maturity',
    title: 'Policy Maturity',
    description: 'Review your uploaded cybersecurity policy, its analysis status, and the evidence contributing to policy maturity.',
  },
  {
    target: 'alerts',
    path: '/dashboard/alerts',
    title: 'Notifications',
    description: 'Review important score, security, verification, vendor, and account changes that may need your attention.',
  },
  {
    target: 'dependencies',
    path: '/dashboard/dependencies',
    title: 'My Dependencies',
    description: 'Manage the vendors your organization depends on, their criticality, and the contact details used for network analysis.',
  },
  {
    target: 'company-settings',
    path: '/dashboard/company-settings',
    title: 'Company Settings',
    description: 'Update your company profile, access controls, and authorized administrators.',
  },
];

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

function DashboardTour({ active, onClose }) {
  const navigate = useNavigate();
  const [stepIndex, setStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState(null);

  const step = TOUR_STEPS[stepIndex];

  const finishTour = useCallback(() => {
    navigate('/dashboard');
    onClose();
  }, [navigate, onClose]);

  useEffect(() => {
    if (!active) {
      setStepIndex(0);
      setTargetRect(null);
      return;
    }

    navigate(step.path);
  }, [active, navigate, step.path]);

  useEffect(() => {
    if (!active) return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') finishTour();
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [active, finishTour]);

  useLayoutEffect(() => {
    if (!active) return undefined;

    const updateTarget = () => {
      const target = document.querySelector(`[data-tour="${step.target}"]`);
      if (!target) {
        setTargetRect(null);
        return;
      }

      const rect = target.getBoundingClientRect();
      setTargetRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        right: rect.right,
        bottom: rect.bottom,
      });
    };

    const frame = window.requestAnimationFrame(updateTarget);
    const timeout = window.setTimeout(updateTarget, 180);
    const interval = window.setInterval(updateTarget, 250);
    window.addEventListener('resize', updateTarget);
    window.addEventListener('scroll', updateTarget, true);

    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timeout);
      window.clearInterval(interval);
      window.removeEventListener('resize', updateTarget);
      window.removeEventListener('scroll', updateTarget, true);
    };
  }, [active, step.target]);

  if (!active || !targetRect) return null;

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const isMobile = viewportWidth <= 768;
  const tooltipWidth = Math.min(360, viewportWidth - 32);
  const tooltipStyle = isMobile
    ? {
        left: 16,
        bottom: 16,
        width: tooltipWidth,
      }
    : {
        left: clamp(targetRect.right + 20, 16, viewportWidth - tooltipWidth - 16),
        top: clamp(targetRect.top - 12, 16, viewportHeight - 250),
        width: tooltipWidth,
      };

  return (
    <div className="dashboard-tour" role="dialog" aria-modal="true" aria-label="Dashboard guide">
      <div
        className="dashboard-tour__spotlight"
        style={{
          top: targetRect.top - 6,
          left: targetRect.left - 6,
          width: targetRect.width + 12,
          height: targetRect.height + 12,
        }}
      />

      <section className="dashboard-tour__card" style={tooltipStyle}>
        <div className="dashboard-tour__progress">
          <span>Dashboard guide</span>
          <span>{stepIndex + 1} of {TOUR_STEPS.length}</span>
        </div>
        <h2>{step.title}</h2>
        <p>{step.description}</p>

        <div className="dashboard-tour__actions">
          <button type="button" className="dashboard-tour__skip" onClick={finishTour}>
            Skip tour
          </button>
          <div>
            {stepIndex > 0 && (
              <button
                type="button"
                className="dashboard-tour__back"
                onClick={() => setStepIndex((current) => current - 1)}
              >
                Back
              </button>
            )}
            <button
              type="button"
              className="dashboard-tour__next"
              onClick={() => {
                if (stepIndex === TOUR_STEPS.length - 1) {
                  finishTour();
                  return;
                }
                setStepIndex((current) => current + 1);
              }}
            >
              {stepIndex === TOUR_STEPS.length - 1 ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

export default DashboardTour;
